<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { useUIStore } from '@/stores/ui'
import { useTenantStore } from '@/stores/tenant'
import { storeToRefs } from 'pinia'
import { ref, onMounted } from 'vue'
import { X } from 'lucide-vue-next'

const { t } = useI18n()
const uiStore = useUIStore()
const tenantStore = useTenantStore()

const { plan, isLoaded } = storeToRefs(tenantStore)
const { showUpgradeModal } = storeToRefs(uiStore)

const STORAGE_KEY = 'lark-automadoc-pro-banner-dismissed'
const proBannerDismissed = ref(false)

onMounted(() => {
  // Check if the pro banner has been dismissed
  proBannerDismissed.value = localStorage.getItem(STORAGE_KEY) === 'true'
})

const dismissProBanner = () => {
  proBannerDismissed.value = true
  localStorage.setItem(STORAGE_KEY, 'true')
}
</script>

<template>
  <!-- Don't render anything until tenant data is loaded -->
  <template v-if="isLoaded">
    <!-- Free Plan Banner -->
    <div v-if="plan === 'free'" class="flex items-center gap-3 px-6 py-3 bg-yellow-50 border-b border-yellow-200">
    <div class="text-lg">🚀</div>
    <div class="flex-1 flex items-center gap-2 text-sm text-gray-800">
      <span>{{ t('freeEditionBanner') }}</span>
    </div>
    <button
      @click="uiStore.openUpgradeModal"
      class="px-4 py-1.5 bg-yellow-500 text-white text-sm font-medium rounded-lg hover:bg-yellow-600 transition-colors">
      {{ t('upgradeToPro') }}
    </button>
  </div>

  <!-- Trial Plan Banner -->
  <div v-else-if="plan === 'trial'" class="flex items-center gap-3 px-6 py-3 bg-yellow-50 border-b border-yellow-200">
    <div class="text-lg">⚠️</div>
    <div class="flex-1 flex items-center gap-2 text-sm text-gray-800">
      <span>{{ t('currentEdition') }}</span>
    </div>
    <button
      @click="uiStore.openUpgradeModal"
      class="px-4 py-1.5 bg-yellow-500 text-white text-sm font-medium rounded-lg hover:bg-yellow-600 transition-colors">
      {{ t('renewNow') }}
    </button>
  </div>

    <!-- Pro Plan Banner -->
    <div v-else-if="plan === 'pro' && !proBannerDismissed" class="flex items-center gap-3 px-6 py-3 bg-yellow-50 border-b border-yellow-200">
      <div class="text-lg">✨</div>
      <div class="flex-1 flex items-center gap-2 text-sm text-gray-800">
        <span>{{ t('proEditionActive') }}</span>
      </div>
      <button
        @click="dismissProBanner"
        class="p-1 text-gray-500 hover:text-gray-700 transition-colors"
        :aria-label="t('close')"
      >
        <X :size="18" />
      </button>
    </div>
  </template>

  <!-- Upgrade Modal -->
  <transition name="fade">
    <div
      v-if="showUpgradeModal"
      class="fixed inset-0 bg-black bg-opacity-50 z-[9999] flex items-center justify-center"
      @click="uiStore.closeUpgradeModal"
    >
      <div
        class="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6"
        @click.stop
      >
        <div class="flex items-center gap-3 mb-4">
          <div class="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
            <span class="text-2xl">🚀</span>
          </div>
          <div>
            <h3 class="text-lg font-semibold text-gray-800">{{ t('upgradeToPro') }}</h3>
          </div>
        </div>
        <p class="text-gray-600 mb-6">
          {{ t('contactAdminForUpgrade') }}
        </p>
        <div class="flex justify-end">
          <button
            @click="uiStore.closeUpgradeModal"
            class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            {{ t('ok') }}
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
